import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserCheck, Trophy } from 'lucide-react';
import AdminLayout from '../../../layouts/AdminLayout';
import { reportsApi } from '../../../services/api';
import { formatCurrency } from '../../../utils/helpers';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function StaffPerformancePage() {
  const { t } = useLanguage();
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = today.slice(0, 8) + '01';
  const [start, setStart] = useState(firstDay);
  const [end, setEnd] = useState(today);

  const { data: staff = [], isLoading } = useQuery({
    queryKey: ['reports-staff', start, end],
    queryFn: () => reportsApi.getByStaff(start, end).then(r => r.data),
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Kinerja Karyawan', 'Staff Performance')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Statistik per karyawan', 'Per-staff statistics')}</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={start} onChange={e => setStart(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44]" />
            <span className="text-slate-400">—</span>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44]" />
          </div>
        </div>

        {/* Cards top performers */}
        {!isLoading && staff.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {staff.slice(0, 3).map((s, i) => (
              <div key={s.staff_id} className={`rounded-2xl border p-5 ${i === 0 ? 'bg-yellow-50 border-yellow-100' : 'bg-white border-slate-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {i === 0 && <Trophy size={16} className="text-yellow-500" />}
                  <p className="font-bold text-slate-800">{s.staff_name}</p>
                </div>
                <p className="text-2xl font-bold text-[#0f6c44]">{formatCurrency(s.kas_diterima)}</p>
                <p className="text-xs text-slate-400 mt-1">{s.orders} {t('pesanan', 'orders')} · {s.shifts} {t('shift', 'shifts')}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-50">
            <UserCheck size={17} className="text-[#0f6c44]" />
            <h2 className="font-semibold text-slate-800">{t('Detail Kinerja', 'Performance Details')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {['#', t('Karyawan', 'Staff'), t('Pesanan Dibuat', 'Orders Created'), t('Omset', 'Revenue'), t('Kas Diterima', 'Cash Collected'), t('Shift', 'Shifts')].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? [1,2,3].map(i => (
                  <tr key={i}>{[1,2,3,4,5,6].map(j => <td key={j} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
                )) : staff.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400 text-sm">{t('Belum ada data', 'No data')}</td></tr>
                ) : staff.map((s, i) => (
                  <tr key={s.staff_id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                    <td className="px-5 py-3.5 text-sm text-slate-400">{i + 1}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-[#0f6c44] text-xs font-bold">{s.staff_name?.charAt(0)}</span>
                        </div>
                        <span className="text-sm font-medium text-slate-800">{s.staff_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700">{s.orders}</td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{formatCurrency(s.omset)}</td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-green-700">{formatCurrency(s.kas_diterima)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-600">{s.shifts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
